import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/react';
import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { clearLocalStorage } from 'src/hooks/useLocalStorage';
import { type LoginResult, useLogin } from 'src/hooks/useLogin';
import { BackendRouter } from 'src/lib/routers';

import { useIdentity } from '../hooks/useIdentity';
import type {
  AccountIdentityAccountFragment,
  UI__IdentityQuery_me_User,
} from '../lib/graphqlTypes/types';

interface AuthContextType {
  user: UI__IdentityQuery_me_User | null;
  account: AccountIdentityAccountFragment | null;
  ssoLoginUrl: string | null;
  isLoggedIn: boolean;
  isUser: boolean;
  loading: boolean;
  error: Error | undefined;
  loggingIn: boolean;
  loginError: Error | undefined;
  refresh: () => void;
  login: (
    email: string,
    password: string,
    recaptchaToken: string,
  ) => Promise<LoginResult>;
  logout: (callbackUrl: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

function clearUtmCookies() {
  const utmParams = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'session_referrer',
    'ajs_user_id',
    'ajs_group_id',
    'ajs_anonymous_id',
    'ajs_user_traits',
    'ajs_group_properties',
    'ajs_user_properties',
    'ajs_event_properties',
    'ajs_event_properties',
    'AMP_unset_4affa0e463',
  ];

  for (const param of utmParams) {
    document.cookie = `${param}=; samesite=lax; path=/; max-age=0`;
  }
}

/**
 * This context provides the global authentication state and methods for the app.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [{ loggingIn, logInSuccessful, error: loginError }, loginAction] =
    useLogin();
  const { me, myAccount, ssoLoginUrl, isLoggedIn, meLoading, error, refresh } =
    useIdentity();

  const login = useCallback(
    async (email: string, password: string, recaptchaToken: string) => {
      const result = await loginAction(email, password, recaptchaToken);
      return result;
    },
    [loginAction],
  );

  useEffect(() => {
    if (logInSuccessful) refresh();
  }, [logInSuccessful, refresh]);

  const value = useMemo(
    () => ({
      user: me,
      isUser: me?.__typename === 'User',
      account: myAccount,
      ssoLoginUrl,
      isLoggedIn,
      loading: meLoading,
      error,
      loginError,
      loggingIn,
      login,
      logout: async (callbackUrl: string) => {
        clearUtmCookies();
        clearLocalStorage();
        Sentry.configureScope((scope) => scope.clear());
        datadogRum.clearUser();
        datadogRum.clearAccount();

        BackendRouter.go('Logout', { callbackUrl });
      },
      refresh,
    }),
    [
      me,
      myAccount,
      ssoLoginUrl,
      isLoggedIn,
      meLoading,
      error,
      login,
      loggingIn,
      loginError,
      refresh,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');

  return context;
}

export type { AuthContextType };
